import classnames from "classnames";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { columnAt } from "modules/spending/workload-plan/components/plan-geometry";
import { PlanGrid, snapStart } from "modules/spending/workload-plan/models/plan-scale";
import moment from "moment";
import { MILESTONE_DAY_FORMAT } from "../models/milestones-store";
import React, { useCallback, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { PlanMilestone, PlanSegment, ProjectPaint } from "../models/project-plan-types";
import styles from "./MilestonesLayer.module.scss";

/**
 * Вехи, выделенные наведением или кликом: одна веха или несколько, схлопнутых
 * в одну ячейку недельного масштаба.
 */
export interface ActiveMilestones {
    /** «проект:ячейка» — по нему повторный клик снимает закрепление */
    key: string;
    projectId: string;
    items: PlanMilestone[];
    /** Экранные координаты вехи: подсказка встаёт над ней (top), окно правки — под ней (bottom) */
    anchor: MilestoneAnchor;
}

/** Точка на экране, у которой открывается подсказка или окно вехи */
export interface MilestoneAnchor {
    x: number;
    top: number;
    bottom: number;
}

export interface MilestoneHandlers {
    /** Наведение — подсказка над вехой; null — курсор ушёл */
    onHover: (active: ActiveMilestones | null) => void;
    /** Клик — фокус на вехе: подсветка столбца и подсказка держатся, пока не кликнут мимо */
    onFocus: (active: ActiveMilestones) => void;
    /** Двойной клик — окно правки вехи */
    onOpen: (active: ActiveMilestones) => void;
}

interface MilestonesLayerProps {
    projectId: string;
    milestones: PlanMilestone[];
    /** Цвет проекта: ромб заливается им, контур — как номер на плашке (светлый на тёмном и наоборот) */
    paint: ProjectPaint;
    /**
     * Полоса проекта: контур вехи подбирается по фону под ней. На полосе — светлый на
     * тёмной, тёмный на светлой; вне полосы фон — белая пустая ячейка, и контур всегда тёмный.
     */
    summary: PlanSegment | null;
    grid: PlanGrid;
    /** Сколько колонок видно — остальные вехи за правым краем */
    columnsCount: number;
    activeKey: string | null;
    handlers: MilestoneHandlers;
    /**
     * Режим правки: одиночную веху можно перетащить. shift — в колонках (рабочих днях);
     * пока промис не выполнен, веха стоит там, куда её бросили
     */
    onMove?: (milestoneId: string, shift: number) => Promise<unknown> | void;
    /**
     * Подложка блока проекта для вертикальных линий вех: она во всю высоту блока, поэтому
     * у свёрнутого проекта линия идёт только по его строке, у раскрытого — и по содержимому.
     * Отсчёт по горизонтали — от начала шкалы. null — линий не рисуем
     */
    linesHost?: HTMLElement | null;
}

/** Сколько пикселей протащить, чтобы нажатие стало перетаскиванием */
const DRAG_THRESHOLD = 3;

interface MilestoneGroup {
    key: string;
    items: PlanMilestone[];
    /** Веха лежит на полосе проекта — фон под ней цвета проекта, а не белый */
    onBar: boolean;
    /** Центр ромба в координатах трека */
    center: number;
}

/**
 * Вехи на строке проекта: ромб в позиции даты. В масштабе «по неделям» вехи одной ячейки
 * схлопываются в ромб со счётчиком. Вехи за краями шкалы не рисуются — о них говорит
 * маркер в колонке названия проекта (HiddenMilestones): она липкая и видна всегда.
 */
export const MilestonesLayer: React.FC<MilestonesLayerProps> = React.memo((props) => {
    const { projectId, milestones, paint, summary, grid, columnsCount, activeKey, handlers, onMove, linesHost } =
        props;

    /** Куда тащат веху (колонка) — и где она стоит после отпускания, пока сервер не ответил */
    const [moving, setMoving] = useState<{ key: string; column: number } | null>(null);
    const [pending, setPending] = useState<{ key: string; column: number } | null>(null);

    const startDrag = useCallback(
        (event: React.PointerEvent<HTMLDivElement>, group: MilestoneGroup) => {
            const track = event.currentTarget.parentElement;

            if (!onMove || event.button !== 0 || group.items.length !== 1 || !track || pending) {
                return;
            }

            // строка проекта по нажатию на полосу начала бы сворачиваться
            event.stopPropagation();

            const milestone = group.items[0];
            const week = grid.scale === "week";
            const origin = milestone.column;
            const startX = event.clientX;
            let active = false;
            let target = origin;

            const onPointerMove = (move: PointerEvent) => {
                if (!active && Math.abs(move.clientX - startX) < DRAG_THRESHOLD) {
                    return;
                }

                active = true;

                const column = columnAt(track, move.clientX, grid);

                if (column >= 0) {
                    // по неделям веха переезжает на тот же день недели соседней недели
                    target = week ? origin + snapStart(grid, column) - snapStart(grid, origin) : column;
                    setMoving({ key: group.key, column: target });
                }
            };

            const onPointerUp = () => {
                window.removeEventListener("pointermove", onPointerMove);
                window.removeEventListener("pointerup", onPointerUp);
                setMoving(null);

                if (!active) {
                    return;
                }

                // клик после перетаскивания не должен ставить фокус и сворачивать строку
                const swallow = (click: MouseEvent) => {
                    click.stopPropagation();
                    click.preventDefault();
                };
                window.addEventListener("click", swallow, { capture: true, once: true });
                window.setTimeout(() => window.removeEventListener("click", swallow, { capture: true }), 0);

                if (target === origin) {
                    return;
                }

                const done = onMove(milestone.id, target - origin);

                if (done && typeof (done as Promise<unknown>).then === "function") {
                    setPending({ key: group.key, column: target });
                    (done as Promise<unknown>).finally(() => setPending(null));
                }
            };

            window.addEventListener("pointermove", onPointerMove);
            window.addEventListener("pointerup", onPointerUp);
        },
        [onMove, grid, pending]
    );

    const groups = useMemo(() => {
        const byKey = new Map<string, MilestoneGroup>();

        milestones.forEach((milestone) => {
            // за краями шкалы не рисуем — о них говорит маркер в колонке названия
            if (milestone.column < 0 || milestone.column >= columnsCount) {
                return;
            }

            // по дням — своя ячейка у каждого дня, по неделям — одна на неделю
            const byWeek = grid.scale === "week";
            const slotIndex = grid.slotOf[milestone.column];
            const slot = grid.slots[slotIndex];
            const key = `${projectId}:${byWeek ? `w${slotIndex}` : `d${milestone.column}`}`;

            const center =
                byWeek && slot
                    ? slot.left + slot.width / 2
                    : grid.offsets[milestone.column] + grid.widths[milestone.column] / 2;

            const group = byKey.get(key);

            if (group) {
                group.items.push(milestone);
            } else {
                const onBar =
                    !!summary &&
                    milestone.column >= summary.start &&
                    milestone.column < summary.start + summary.length;

                byKey.set(key, { key, items: [milestone], center, onBar });
            }
        });

        return Array.from(byKey.values());
    }, [milestones, grid, columnsCount, projectId, summary]);

    const activate = (group: MilestoneGroup, element: HTMLElement): ActiveMilestones => {
        const rect = element.getBoundingClientRect();

        return {
            key: group.key,
            projectId,
            items: group.items,
            anchor: { x: rect.left + rect.width / 2, top: rect.top, bottom: rect.bottom },
        };
    };

    /** Колонка вехи: тащат — под курсором, бросили — там, пока сервер не ответил */
    const columnOf = (group: MilestoneGroup) =>
        moving && moving.key === group.key
            ? moving.column
            : pending && pending.key === group.key
            ? pending.column
            : group.items[0].column;

    const leftOf = (group: MilestoneGroup) =>
        (moving && moving.key === group.key) || (pending && pending.key === group.key)
            ? milestoneCenter(grid, columnOf(group))
            : group.center;

    return (
        <>
            {linesHost &&
                createPortal(
                    groups.map((group) => {
                        const span = milestoneSpan(grid, columnOf(group));
                        const active = group.key === activeKey || (!!moving && moving.key === group.key);

                        return (
                            <React.Fragment key={group.key}>
                                {/* день вехи (неделя — в недельном масштабе) бледным столбцом её цвета */}
                                <div
                                    className={classnames(styles.band, { [styles.bandActive]: active })}
                                    style={{ left: span.left, width: span.width, backgroundColor: paint.milestoneFill }}
                                />
                                <div
                                    className={classnames(styles.line, { [styles.lineActive]: active })}
                                    style={{ left: leftOf(group), backgroundColor: paint.milestoneFill }}
                                />
                            </React.Fragment>
                        );
                    }),
                    linesHost
                )}

            {groups.map((group) => (
                <div
                    key={group.key}
                    className={classnames(styles.milestone, {
                        [styles.active]: group.key === activeKey,
                        [styles.draggable]: !!onMove && group.items.length === 1,
                        [styles.moving]: !!moving && moving.key === group.key,
                        [styles.many]: group.items.length > 1,
                        // полоса полупрозрачная — её тёмность считается по смешанному с белым цвету
                        [styles.onDark]: group.onBar && paint.dark,
                        [styles.onLight]: !(group.onBar && paint.dark),
                    })}
                    style={
                        {
                            left: leftOf(group),
                            "--milestone-color": paint.chipBackground,
                            "--milestone-fill": paint.milestoneFill,
                        } as React.CSSProperties
                    }
                    onPointerDown={onMove && group.items.length === 1 ? (event) => startDrag(event, group) : undefined}
                    onMouseEnter={(event) => handlers.onHover(activate(group, event.currentTarget))}
                    onMouseLeave={() => handlers.onHover(null)}
                    onClick={(event) => {
                        // строка проекта по клику сворачивается — веху это не касается
                        event.stopPropagation();
                        handlers.onFocus(activate(group, event.currentTarget));
                    }}
                    onDoubleClick={(event) => {
                        // двойной клик по полосе — новая веха; по самой вехе — её правка
                        event.stopPropagation();
                        handlers.onOpen(activate(group, event.currentTarget));
                    }}
                    data-milestone="true"
                >
                    <span
                        className={styles.diamond}
                        style={{ backgroundColor: paint.milestoneFill }}
                    />
                    {group.items.length > 1 && (
                        // счётчик тёмный с белыми цифрами: на полосе цвета проекта он терялся
                        <span className={styles.count}>{group.items.length}</span>
                    )}
                </div>
            ))}

        </>
    );
});

MilestonesLayer.displayName = "MilestonesLayer";

/** Отступ маркера скрытых вех от края видимой части строки */
const EDGE_GAP = 4;

/** Видимый кусок шкалы в координатах трека (без зума): от левого до правого края экрана */
export interface TrackViewport {
    start: number;
    end: number;
}

/** Ячейка вехи в координатах трека: день, а в недельном масштабе — вся неделя */
function milestoneSpan(grid: PlanGrid, column: number): { left: number; width: number } {
    if (grid.scale === "week") {
        const slot = grid.slots[grid.slotOf[column]];

        if (slot) {
            return { left: slot.left, width: slot.width };
        }
    }

    return { left: grid.offsets[column], width: grid.widths[column] };
}

/** Центр ромба вехи в координатах трека: день — центр дня, неделя — центр недели */
export function milestoneCenter(grid: PlanGrid, column: number): number {
    if (grid.scale === "week") {
        const slot = grid.slots[grid.slotOf[column]];

        if (slot) {
            return slot.left + slot.width / 2;
        }
    }

    return grid.offsets[column] + grid.widths[column] / 2;
}

interface HiddenMilestonesProps {
    milestones: PlanMilestone[];
    /** Цвет проекта — кружок в маркере такой же, как у вех на строке */
    paint: ProjectPaint;
    grid: PlanGrid;
    columnsCount: number;
    /** Что сейчас на экране; null — ещё не измерено, считаем видимой всю шкалу */
    viewport: TrackViewport | null;
    /** Прокрутить к вехе, скрытой прокруткой */
    onReveal: (center: number) => void;
}

/**
 * Сколько вех не видно слева и справа — маркерами поверх строки дней, у краёв её видимой
 * части: они едут вместе с прокруткой и всегда на экране. Считаются и вехи за горизонтом загруженной шкалы, и вехи, ушедшие
 * за край экрана при горизонтальной прокрутке. Клик по маркеру прокручивает к ближайшей
 * скрытой прокруткой вехе.
 */
export const HiddenMilestones: React.FC<HiddenMilestonesProps> = ({
    milestones,
    paint,
    grid,
    columnsCount,
    viewport,
    onReveal,
}) => {
    const left: PlanMilestone[] = [];
    const right: PlanMilestone[] = [];
    /** Ближайшие к экрану вехи, до которых можно докрутить */
    let nearestLeft: number | null = null;
    let nearestRight: number | null = null;

    milestones.forEach((milestone) => {
        if (milestone.column < 0) {
            left.push(milestone);
            return;
        }

        if (milestone.column >= columnsCount) {
            right.push(milestone);
            return;
        }

        if (!viewport) {
            return;
        }

        const center = milestoneCenter(grid, milestone.column);

        if (center < viewport.start) {
            left.push(milestone);
            nearestLeft = nearestLeft === null ? center : Math.max(nearestLeft, center);
        } else if (center > viewport.end) {
            right.push(milestone);
            nearestRight = nearestRight === null ? center : Math.min(nearestRight, center);
        }
    });

    if (!left.length && !right.length) {
        return null;
    }

    const describe = (list: PlanMilestone[]) =>
        list.map((milestone) => `${milestoneDate(milestone)} — ${milestone.name}`).join("\n");

    const reveal = (center: number | null) => (event: React.MouseEvent) => {
        // строка проекта по клику сворачивается — маркер это не касается
        event.stopPropagation();
        center !== null && onReveal(center);
    };

    // маркеры держатся у краёв видимой части строки: левый — сразу за колонкой названий,
    // правый — у правого края экрана (но не дальше конца шкалы)
    const edgeLeft = viewport ? Math.max(0, viewport.start) : 0;
    const edgeRight = viewport ? Math.min(grid.totalWidth, viewport.end) : grid.totalWidth;

    return (
        <>
            {left.length > 0 && (
                <span
                    className={classnames(styles.hidden, styles.hiddenLeft, {
                        [styles.hiddenScroll]: nearestLeft !== null,
                    })}
                    style={{ left: edgeLeft + EDGE_GAP }}
                    data-milestone="true"
                    data-tip={`Вех левее видимой части: ${left.length}`}
                    data-tip-note={describe(left)}
                    onClick={reveal(nearestLeft)}
                    onDoubleClick={(event) => event.stopPropagation()}
                >
                    <GeneralIcon type="general-chevron-left" size={16} className={styles.hiddenArrow} />
                    <span
                        className={styles.hiddenDot}
                        style={{ backgroundColor: paint.milestoneFill }}
                    />
                    <span className={styles.hiddenCount}>{left.length}</span>
                </span>
            )}

            {right.length > 0 && (
                <span
                    className={classnames(styles.hidden, styles.hiddenRight, {
                        [styles.hiddenScroll]: nearestRight !== null,
                    })}
                    style={{ left: edgeRight - EDGE_GAP }}
                    data-milestone="true"
                    data-tip={`Вех правее видимой части: ${right.length}`}
                    data-tip-note={describe(right)}
                    onClick={reveal(nearestRight)}
                    onDoubleClick={(event) => event.stopPropagation()}
                >
                    <span
                        className={styles.hiddenDot}
                        style={{ backgroundColor: paint.milestoneFill }}
                    />
                    <span className={styles.hiddenCount}>{right.length}</span>
                    <GeneralIcon type="general-chevron-right" size={16} className={styles.hiddenArrow} />
                </span>
            )}
        </>
    );
};

/** Дата вехи для подписи: 20.11.2026 */
export function milestoneDate(milestone: { day: string }): string {
    return moment(milestone.day, MILESTONE_DAY_FORMAT).format("DD.MM.YYYY");
}
